import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { formatTokenAmount } from "../lib/formatAmount";
import { MoneyFlowStepper, type MoneyFlowStep } from "../components/MoneyFlowStepper";
import { REQUIRED_CHAIN_ID, REQUIRED_CHAIN_NAME } from "../config";
import {
  approveAffiliateWithdrawal,
  createAffiliateWithdrawalProposal,
  getAffiliateWithdrawal,
  getAffiliateWithdrawalProposal,
  payAffiliateWithdrawal,
  rejectAffiliateWithdrawal,
  type AffiliatePayoutProposal,
  type AffiliateWithdrawalRow,
} from "../api/affiliatePayouts";
import { mapAffiliateError } from "../api/affiliatePayoutsErrors";

// Affiliate-withdrawal detail.
//
// Surface for a single queue row + three operator mutations:
//
//   1. Approve  — money_movement; requires an APPROVED multisign
//                 proposalId. The operator types the proposalId here
//                 after collecting quorum at /approvals/:id. We do
//                 NOT create proposals on this page; the BE owns
//                 that surface.
//   2. Pay      — money_movement; broadcasts the on-chain payout
//                 through the BE signer facade. Same proposalId.
//                 High-risk: confirm modal is amber-red, button copy
//                 is explicit, dialog re-renders the exact amount +
//                 destination + proposalId the FE is about to POST.
//   3. Reject   — operator-records / low_config. Requires a reason
//                 string (free-text, BE truncates).
//
// The detail render only displays BE-supplied, audit-safe metadata.
// Buyer identifiers (wallet / email / userAgent / IP) are never
// requested or rendered.

type Dialog =
  | { kind: "none" }
  | { kind: "approve" }
  | { kind: "pay" }
  | { kind: "reject" };

function AffiliateWithdrawalDetailInner({ id }: { id: string }) {
  const [detail, setDetail] = useState<AffiliateWithdrawalRow | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [proposalIdInput, setProposalIdInput] = useState("");
  const [reasonInput, setReasonInput] = useState("");

  const [dialog, setDialog] = useState<Dialog>({ kind: "none" });
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Post-mutation banners. `successNotice` confirms the BE accepted
  // the action; `refetchWarning` is set when the follow-up GET to
  // /:id failed AFTER the mutation succeeded — the operator needs to
  // reload to see the authoritative current state, but the action
  // itself is not in doubt.
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [refetchWarning, setRefetchWarning] = useState<string | null>(null);

  // Create-proposal flow state. Creating the money_movement proposal is a
  // browser-session call to the per-row BFF route; it mints a `proposed`
  // proposal and moves no funds (the 2-of-N quorum + payload-match still
  // gate approve/pay). On success we auto-fill the proposalId input.
  const [creatingProposal, setCreatingProposal] = useState(false);
  const [createdProposalId, setCreatedProposalId] = useState<string | null>(null);
  const [createProposalError, setCreateProposalError] = useState<string | null>(null);

  // Auto-linked payout proposal (BE-owned match). Fetched separately from the
  // row so a proposal-endpoint hiccup never blanks the withdrawal detail.
  const [payoutProposal, setPayoutProposal] = useState<AffiliatePayoutProposal | null>(null);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);

  const loadProposal = useCallback(async () => {
    setProposalLoading(true);
    setProposalError(null);
    try {
      const r = await getAffiliateWithdrawalProposal(id);
      setPayoutProposal(r.payoutProposal);
    } catch (e) {
      setProposalError(mapAffiliateError(e).message);
    } finally {
      setProposalLoading(false);
    }
  }, [id]);

  async function handleCreateProposal() {
    if (!detail) return;
    setCreatingProposal(true);
    setCreateProposalError(null);
    try {
      const res = await createAffiliateWithdrawalProposal(detail.id);
      setCreatedProposalId(res.proposalId);
      // The new proposal is `proposed` (0/2). Re-link so the card shows it.
      await loadProposal();
    } catch (e) {
      setCreateProposalError(mapAffiliateError(e).message);
    } finally {
      setCreatingProposal(false);
    }
  }

  const load = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const r = await getAffiliateWithdrawal(id);
      setDetail(r);
    } catch (e) {
      setLoadError(mapAffiliateError(e).message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
    void loadProposal();
  }, [load, loadProposal]);

  // Refetch the detail row without disturbing the initial-load error
  // surface. Returns true on success, false otherwise; the caller
  // decides what to show when the refetch fails. Critically, the
  // browser's source of truth for the row's current state is the
  // BE — we never trust the action-response body to be the full row.
  async function refetchDetail(): Promise<boolean> {
    try {
      const r = await getAffiliateWithdrawal(id);
      setDetail(r);
      return true;
    } catch {
      return false;
    }
  }

  function openDialog(d: Dialog) {
    setDialogError(null);
    setDialog(d);
  }
  function closeDialog() {
    if (busy) return;
    setDialog({ kind: "none" });
    setDialogError(null);
  }

  /**
   * Run a mutation, then refetch the detail. On mutation success the
   * dialog closes and a success notice surfaces. On refetch failure
   * the notice stays — we do NOT pretend the mutation didn't happen
   * — and a separate warning tells the operator to reload to see
   * authoritative current state.
   */
  async function performMutation(
    successLabel: string,
    mutate: () => Promise<unknown>,
  ): Promise<void> {
    setBusy(true);
    setDialogError(null);
    try {
      await mutate();
    } catch (e) {
      setDialogError(mapAffiliateError(e).message);
      setBusy(false);
      return;
    }
    // Mutation succeeded. Close the dialog, post the notice, then
    // attempt the refetch. setBusy(false) is deferred until after
    // the refetch so the modal cannot reopen and double-fire.
    setDialog({ kind: "none" });
    setSuccessNotice(successLabel);
    setRefetchWarning(null);
    void loadProposal();
    const refetched = await refetchDetail();
    if (!refetched) {
      setRefetchWarning(
        `${successLabel} The current row state could not be refreshed — please reload the page to see authoritative state.`,
      );
    }
    setBusy(false);
  }

  async function runApprove() {
    if (!detail) return;
    // Prefer the auto-linked usable proposal; fall back to the Advanced
    // manual id only when there is no usable auto proposal.
    const pid = payoutProposal?.isUsableForApprove
      ? payoutProposal.id
      : proposalIdInput.trim();
    await performMutation("Withdrawal approved.", () =>
      approveAffiliateWithdrawal(detail.id, { proposalId: pid }),
    );
  }

  async function runPay() {
    if (!detail) return;
    const pid = payoutProposal?.isUsableForPay
      ? payoutProposal.id
      : proposalIdInput.trim();
    await performMutation("Withdrawal paid — on-chain broadcast accepted.", () =>
      payAffiliateWithdrawal(detail.id, { proposalId: pid }),
    );
  }

  async function runReject() {
    if (!detail) return;
    await performMutation("Withdrawal rejected.", () =>
      rejectAffiliateWithdrawal(detail.id, {
        reason: reasonInput.trim(),
      }),
    );
  }

  if (loading && !detail) {
    return <p style={{ padding: 12, color: "var(--fg-dim)" }}>Loading…</p>;
  }
  if (loadError && !detail) {
    return (
      <div style={{ padding: 12 }}>
        <ErrorBox text={loadError} />
        <p style={{ marginTop: 12 }}>
          <Link to="/admin/affiliate/withdrawals" style={{ color: "var(--accent, #2c7be5)" }}>
            ← Back to queue
          </Link>
        </p>
      </div>
    );
  }
  if (!detail) return null;

  // proposalId hygiene: the operator must paste the APPROVED admin-action
  // proposalId (from Approvals), NOT a queue-row id. `affw_` is a withdrawal
  // row id and `ard_` a redemption row id — pasting either is the most common
  // mistake (and the BE would reject it as `proposal_unknown`). Block both
  // client-side so Approve/Pay stay disabled and the operator gets a precise
  // inline hint instead of a round-trip 403.
  // Canonical money rendering for this row. Affiliate amounts are USD-cents
  // minor units (decimals=2): 5970 → "59.70 USD" + "5,970 minor". Never show
  // the bare integer alone on a money-moving surface.
  const money = formatTokenAmount(detail.amountMinor, 2, detail.currency || "USD", "minor");

  const proposalIdRaw = proposalIdInput.trim();
  const proposalIdLooksLikeQueueRowId = /^(affw_|ard_)/i.test(proposalIdRaw);
  const proposalIdValid = proposalIdRaw.length > 0 && !proposalIdLooksLikeQueueRowId;
  const reasonValid = reasonInput.trim().length > 0;
  const canApprove = detail.status === "queued";
  const canPay = detail.status === "approved";
  const canReject = detail.status === "queued" || detail.status === "approved";

  // ── Auto-linked proposal drives the normal flow ──────────────────────────
  // Approve/Pay use the BE-matched `payoutProposal.id` automatically. The
  // manual fallback (Advanced only) is used solely when no usable auto
  // proposal is present AND the operator pasted a valid id.
  const autoApproveProposalId = payoutProposal?.isUsableForApprove ? payoutProposal.id : "";
  const autoPayProposalId = payoutProposal?.isUsableForPay ? payoutProposal.id : "";
  const approveProposalId = autoApproveProposalId || (proposalIdValid ? proposalIdRaw : "");
  const payProposalId = autoPayProposalId || (proposalIdValid ? proposalIdRaw : "");
  const canApproveAction = canApprove && approveProposalId.length > 0;
  const canPayAction = canPay && payProposalId.length > 0;
  const approveHint = canApproveAction
    ? ""
    : payoutProposal
      ? "Proposal is not approved 2-of-2 yet"
      : "Create a payout proposal and collect 2-of-2 approvals first";
  const payHint = canPayAction
    ? ""
    : payoutProposal
      ? "Proposal is not approved 2-of-2 yet"
      : "Create a payout proposal and collect 2-of-2 approvals first";
  // A terminal-unusable proposal (or none) means the operator should create
  // a fresh one — surface the Create button in those states only.
  const proposalTerminalUnusable =
    payoutProposal !== null &&
    (payoutProposal.status === "expired" ||
      payoutProposal.status === "cancelled" ||
      payoutProposal.status === "rejected");
  const showCreateProposal =
    !proposalLoading && !proposalError && (payoutProposal === null || proposalTerminalUnusable);
  const rejected = detail.status === "rejected";
  const paid = detail.status === "paid";

  const steps: ReadonlyArray<MoneyFlowStep> = [
    {
      label: "Review request",
      hint: "Canonical amount / destination / identity (read-only).",
      state: "done",
    },
    {
      label: "Provide payout proposal",
      hint: "Approved money_movement proposalId (paste — fallback).",
      state: rejected ? "blocked" : proposalIdValid || paid ? "done" : "current",
    },
    {
      label: "Approve request",
      hint: "queued → approved (records operator approval).",
      state: rejected
        ? "blocked"
        : paid || detail.status === "approved"
          ? "done"
          : canApprove && proposalIdValid
            ? "current"
            : "upcoming",
    },
    {
      label: "Execute payout",
      hint: "approved → paid (broadcasts on-chain to user destination).",
      state: rejected ? "blocked" : paid ? "done" : canPay && proposalIdValid ? "current" : "upcoming",
    },
  ];

  return (
    <div>
      <p style={{ margin: "8px 0", fontSize: 12 }}>
        <Link to="/admin/affiliate/withdrawals" style={{ color: "var(--accent, #2c7be5)" }}>
          ← All withdrawals
        </Link>
      </p>

      <div
        role="note"
        style={{
          margin: "8px 0",
          padding: "10px 12px",
          border: "1px solid var(--accent, #2c7be5)",
          background: "rgba(44, 123, 229, 0.06)",
          color: "var(--accent, #2c7be5)",
          borderRadius: 4,
          fontSize: 12.5,
          fontWeight: 600,
        }}
      >
        Affiliate payout sends USDC to the user destination below — this is NOT a
        treasury withdrawal.
      </div>

      <MoneyFlowStepper steps={steps} ariaLabel="Affiliate payout steps" />

      {successNotice && (
        <div
          role="status"
          style={{
            padding: 10,
            border: "1px solid var(--ok, #0a9444)",
            background: "rgba(10, 148, 68, 0.06)",
            color: "var(--ok, #0a9444)",
            borderRadius: 4,
            fontSize: 13,
            margin: "8px 0",
          }}
        >
          ✓ {successNotice}
        </div>
      )}
      {refetchWarning && (
        <div
          role="alert"
          style={{
            padding: 10,
            border: "1px solid var(--warn, #b58900)",
            background: "rgba(251, 191, 36, 0.08)",
            color: "var(--warn, #b58900)",
            borderRadius: 4,
            fontSize: 13,
            margin: "8px 0",
          }}
        >
          {refetchWarning}
        </div>
      )}

      <Section title="Queue row">
        <DetailGrid w={detail} />
      </Section>

      <Section title="Status timeline">
        <Timeline w={detail} />
      </Section>

      {(canApprove || canPay) && (
        <Section title="Canonical payout payload (read-only)">
          <p style={{ margin: "0 0 8px 0", fontSize: 12, color: "var(--fg-dim)" }}>
            These values are fixed by the user's request and the BE config. They
            cannot be edited here. If any value is wrong,{" "}
            <strong>reject the request</strong> and have the user submit a new
            one — never alter the amount or destination of a payout.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <tbody>
              <Row k="withdrawalId" v={<code>{detail.id}</code>} />
              <Row k="identityId" v={<code>{detail.identityId}</code>} />
              <Row
                k="amount"
                v={
                  <>
                    <strong>{money.display}</strong>{" "}
                    <span style={{ color: "var(--fg-dim)" }}>({money.raw})</span>
                  </>
                }
              />
              <Row k="destination" v={<code>{detail.destination}</code>} />
              <Row
                k="token"
                v={
                  <span style={{ color: "var(--fg-dim)" }}>
                    USDC — resolved by the BE at execute time
                  </span>
                }
              />
              <Row
                k="chain"
                v={
                  <>
                    {REQUIRED_CHAIN_NAME}{" "}
                    <span style={{ color: "var(--fg-dim)" }}>(chain id {REQUIRED_CHAIN_ID})</span>
                  </>
                }
              />
            </tbody>
          </table>
        </Section>
      )}

      {(canApprove || canPay) && (
        <Section title="Money-movement proposal">
          <p style={{ margin: "0 0 10px 0", fontSize: 12, color: "var(--fg-dim)" }}>
            This row is matched to its multisign proposal automatically — the BE
            binds the canonical amount / destination / token / chain. You never
            paste a proposalId in the normal flow: create the proposal, collect
            2-of-2 owner approvals in Approvals, then Approve and Pay below.
          </p>

          {/* Auto-link status card */}
          {proposalError ? (
            <div
              role="alert"
              style={{
                margin: "8px 0",
                padding: 10,
                border: "1px solid var(--danger, #d23f3f)",
                background: "rgba(210, 63, 63, 0.06)",
                color: "var(--danger, #d23f3f)",
                borderRadius: 4,
                fontSize: 12.5,
              }}
            >
              Could not load the payout proposal: {proposalError}{" "}
              <button
                type="button"
                onClick={() => void loadProposal()}
                style={{ marginLeft: 6, fontSize: 12 }}
              >
                Retry
              </button>
            </div>
          ) : proposalLoading && !payoutProposal ? (
            <p style={{ margin: "8px 0", fontSize: 12, color: "var(--fg-dim)" }}>
              Checking for a matching proposal…
            </p>
          ) : payoutProposal === null ? (
            <p style={{ margin: "8px 0", fontSize: 12.5, color: "var(--fg-dim)" }}>
              No payout proposal for this row yet. Create one, then collect
              2-of-2 owner approvals in Approvals.
            </p>
          ) : payoutProposal.status === "proposed" ? (
            <div
              role="status"
              style={{
                margin: "8px 0",
                padding: 10,
                border: "1px solid var(--warn, #b58900)",
                background: "rgba(251, 191, 36, 0.08)",
                color: "var(--warn, #b58900)",
                borderRadius: 4,
                fontSize: 12.5,
              }}
            >
              <strong>
                Waiting for owner approvals — {payoutProposal.approvalsCount}/
                {payoutProposal.thresholdRequired}.
              </strong>{" "}
              <code>{payoutProposal.id}</code> ·{" "}
              <Link
                to={`/approvals/${encodeURIComponent(payoutProposal.id)}`}
                style={{ color: "var(--accent, #2c7be5)" }}
              >
                Open in Approvals →
              </Link>
            </div>
          ) : payoutProposal.status === "approved" && payoutProposal.isApproved ? (
            <div
              role="status"
              style={{
                margin: "8px 0",
                padding: 10,
                border: "1px solid var(--ok, #0a9444)",
                background: "rgba(10, 148, 68, 0.06)",
                color: "var(--ok, #0a9444)",
                borderRadius: 4,
                fontSize: 12.5,
              }}
            >
              <strong>
                ✓ Approved {payoutProposal.approvalsCount}/
                {payoutProposal.thresholdRequired} — ready.
              </strong>{" "}
              <code>{payoutProposal.id}</code> · the proposalId is applied
              automatically by the {canPay ? "Pay" : "Approve"} button below.
            </div>
          ) : payoutProposal.status === "executed" ? (
            <div
              role="status"
              style={{
                margin: "8px 0",
                padding: 10,
                border: "1px solid var(--ok, #0a9444)",
                background: "rgba(10, 148, 68, 0.06)",
                color: "var(--ok, #0a9444)",
                borderRadius: 4,
                fontSize: 12.5,
              }}
            >
              <strong>Proposal executed.</strong>{" "}
              {payoutProposal.executedTxHash ? (
                <>
                  txHash <code>{payoutProposal.executedTxHash}</code>
                </>
              ) : null}
            </div>
          ) : (
            <div
              role="alert"
              style={{
                margin: "8px 0",
                padding: 10,
                border: "1px solid var(--warn, #b58900)",
                background: "rgba(251, 191, 36, 0.08)",
                color: "var(--warn, #b58900)",
                borderRadius: 4,
                fontSize: 12.5,
              }}
            >
              Proposal {payoutProposal.status} — it can no longer be used.
              Create a new payout proposal.
            </div>
          )}

          {showCreateProposal && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
              <button
                type="button"
                onClick={() => void handleCreateProposal()}
                disabled={creatingProposal}
                style={primaryBtnStyle(!creatingProposal)}
              >
                {creatingProposal ? "Creating…" : "Create payout proposal"}
              </button>
            </div>
          )}

          {createProposalError && (
            <p
              role="alert"
              style={{
                margin: "8px 0 0 0",
                padding: 8,
                border: "1px solid var(--danger, #d23f3f)",
                background: "rgba(210, 63, 63, 0.06)",
                color: "var(--danger, #d23f3f)",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              {createProposalError}
            </p>
          )}

          {createdProposalId && (
            <p style={{ margin: "8px 0 0 0", fontSize: 12, color: "var(--ok, #0a9444)" }}>
              ✓ Proposal created: <code>{createdProposalId}</code> ·{" "}
              <Link
                to={`/approvals/${encodeURIComponent(createdProposalId)}`}
                style={{ color: "var(--accent, #2c7be5)" }}
              >
                approve it in Approvals →
              </Link>
            </p>
          )}

          <details style={{ marginTop: 12 }}>
            <summary style={{ fontSize: 12, color: "var(--fg-dim)", cursor: "pointer" }}>
              Advanced / manual fallback — paste an approved proposalId
            </summary>
            <p style={{ margin: "8px 0", fontSize: 12, color: "var(--fg-dim)" }}>
              Use only if you created the proposal out of band. <strong>Do not</strong>{" "}
              use a Treasury proposal or a bucket-level affiliate proposal — their
              payload is not bound to this queue row and the BE payload-match guard
              will reject it.
            </p>
            <input
              type="text"
              value={proposalIdInput}
              onChange={(e) => setProposalIdInput(e.target.value.trim())}
              placeholder="ulid of an APPROVED proposal"
              aria-invalid={proposalIdLooksLikeQueueRowId}
              style={{
                width: "100%",
                padding: "6px 8px",
                fontFamily: "monospace",
                fontSize: 12,
                border: proposalIdLooksLikeQueueRowId
                  ? "1px solid var(--danger, #d23f3f)"
                  : undefined,
              }}
            />
            {proposalIdLooksLikeQueueRowId && (
              <p
                role="alert"
                style={{ margin: "6px 0 0 0", fontSize: 12, color: "var(--danger, #d23f3f)" }}
              >
                This is a withdrawalId, not a proposalId. Copy the proposal id
                from Approvals.
              </p>
            )}
          </details>
        </Section>
      )}

      <Section title="Actions">
        {/* Rejection reason is ALWAYS rendered (and editable) while the row
            is rejectable — never hidden behind the confirm overlay. The
            operator types the reason first; the Reject button only enables
            once it is non-empty, so the confirm modal opens with the reason
            already captured. Works on mobile + desktop (no input is ever
            covered by the modal). */}
        {canReject && (
          <div style={{ marginBottom: 12 }}>
            <label
              htmlFor="reject-reason"
              style={{ fontSize: 12, color: "var(--fg-dim)", display: "block" }}
            >
              Reason (required, recorded in the BE audit log)
            </label>
            <textarea
              id="reject-reason"
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
              rows={3}
              placeholder="e.g. duplicate of req_…, fraud signal from team-X"
              style={{ width: "100%", fontSize: 12, marginTop: 4, padding: 6 }}
            />
          </div>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canApprove && (
            <button
              type="button"
              onClick={() => openDialog({ kind: "approve" })}
              disabled={!canApproveAction}
              title={approveHint}
              style={primaryBtnStyle(canApproveAction)}
            >
              Approve
            </button>
          )}
          {canPay && (
            <button
              type="button"
              onClick={() => openDialog({ kind: "pay" })}
              disabled={!canPayAction}
              title={payHint}
              style={dangerBtnStyle(canPayAction)}
            >
              Pay (on-chain)
            </button>
          )}
          {canReject && (
            <button
              type="button"
              onClick={() => openDialog({ kind: "reject" })}
              disabled={!reasonValid}
              title={reasonValid ? "" : "Enter a rejection reason first"}
              style={neutralBtnStyle(reasonValid)}
            >
              Reject
            </button>
          )}
          {!canApprove && !canPay && !canReject && (
            <p style={{ margin: 0, fontSize: 12, color: "var(--fg-dim)" }}>
              Row is in terminal state — view only.
            </p>
          )}
        </div>
      </Section>

      <ConfirmDialog
        open={dialog.kind === "approve"}
        title="Approve affiliate withdrawal"
        description={
          <p style={{ margin: 0 }}>
            Records operator-approval of this queued withdrawal against an
            approved multisign proposal. Pay is a separate explicit click — no
            auto-advance.
          </p>
        }
        rows={[
          { key: "withdrawalId", value: <code>{detail.id}</code> },
          {
            key: "amount",
            value: (
              <>
                <strong>{money.display}</strong>{" "}
                <span style={{ color: "var(--fg-dim)" }}>({money.raw})</span>
              </>
            ),
          },
          { key: "destination", value: <code>{detail.destination}</code> },
          { key: "proposalId", value: <code>{approveProposalId || proposalIdInput.trim()}</code> },
        ]}
        confirmLabel="Approve"
        tone="normal"
        busy={busy}
        error={dialogError}
        onConfirm={() => void runApprove()}
        onCancel={closeDialog}
      />

      <ConfirmDialog
        open={dialog.kind === "pay"}
        title="Pay affiliate withdrawal — broadcasts on-chain"
        description={
          <>
            <p style={{ margin: "0 0 6px 0" }}>
              This triggers an on-chain payout through the BE signer. The
              browser does not sign or broadcast — the BE re-checks the
              approved proposal's payload byte-for-byte before broadcast.
            </p>
            <p style={{ margin: 0, color: "var(--danger, #d23f3f)" }}>
              Verify amount + destination + proposalId before confirming.
            </p>
          </>
        }
        rows={[
          { key: "withdrawalId", value: <code>{detail.id}</code> },
          {
            key: "amount",
            value: (
              <>
                <strong style={{ fontSize: 15 }}>{money.display}</strong>{" "}
                <span style={{ color: "var(--fg-dim)" }}>({money.raw})</span>
              </>
            ),
          },
          { key: "token / chain", value: <span>{money.symbol} · {REQUIRED_CHAIN_NAME} (chain {REQUIRED_CHAIN_ID})</span> },
          { key: "destination", value: <code>{detail.destination}</code> },
          { key: "proposalId", value: <code>{payProposalId || proposalIdInput.trim()}</code> },
        ]}
        requireTypedConfirmation={{
          expected: money.human,
          label: `Type the amount "${money.human}" to confirm this on-chain payout`,
          placeholder: money.human,
        }}
        confirmLabel="Pay (broadcast)"
        tone="danger"
        busy={busy}
        error={dialogError}
        onConfirm={() => void runPay()}
        onCancel={closeDialog}
      />

      <ConfirmDialog
        open={dialog.kind === "reject"}
        title="Reject affiliate withdrawal"
        description={
          <p style={{ margin: 0 }}>
            Records operator-rejection. No on-chain effect, no funds moved.
            The reason is persisted in the BE audit log.
          </p>
        }
        rows={[
          { key: "withdrawalId", value: <code>{detail.id}</code> },
          {
            key: "amount",
            value: (
              <>
                <strong>{money.display}</strong>{" "}
                <span style={{ color: "var(--fg-dim)" }}>({money.raw})</span>
              </>
            ),
          },
          {
            key: "reason",
            value: reasonValid ? (
              <code style={{ whiteSpace: "pre-wrap" }}>{reasonInput.trim()}</code>
            ) : (
              <em style={{ color: "var(--danger, #d23f3f)" }}>required</em>
            ),
          },
        ]}
        confirmLabel="Reject"
        tone="warning"
        busy={busy}
        confirmDisabled={!reasonValid}
        error={dialogError}
        onConfirm={() => void runReject()}
        onCancel={closeDialog}
      />
    </div>
  );
}

export function AffiliateWithdrawalDetail() {
  const { id } = useParams<{ id: string }>();
  return (
    <div>
      <PageHeader
        title={`Affiliate withdrawal — ${id ?? ""}`}
        subtitle="Approve · Pay · Reject. Money-movement actions require an APPROVED multisign proposalId."
        method="GET / POST"
        path="/v2/admin/bff/affiliate/withdrawals/:id"
      />
      <AuthRequired>
        {id ? <AffiliateWithdrawalDetailInner id={id} /> : <ErrorBox text="Missing :id in URL" />}
      </AuthRequired>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: "14px 16px",
        margin: "16px 0",
        background: "var(--bg-card)",
      }}
    >
      <h3 style={{ margin: "0 0 12px 0", fontSize: 14 }}>{title}</h3>
      {children}
    </section>
  );
}

function DetailGrid({ w }: { w: AffiliateWithdrawalRow }) {
  const money = formatTokenAmount(w.amountMinor, 2, w.currency || "USD", "minor");
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <tbody>
        <Row k="id" v={<code>{w.id}</code>} />
        <Row k="identityId" v={<code>{w.identityId}</code>} />
        <Row k="status" v={<strong>{w.status}</strong>} />
        <Row
          k="amount"
          v={
            <>
              <strong>{money.display}</strong>{" "}
              <span style={{ color: "var(--fg-dim)" }}>({money.raw})</span>
            </>
          }
        />
        <Row k="destination" v={<code>{w.destination}</code>} />
        <Row k="proposalId" v={w.proposalId ? <code>{w.proposalId}</code> : "—"} />
        <Row
          k="txHash"
          v={
            w.txHash ? (
              <>
                <code>{w.txHash}</code>
                <a
                  href={`https://sepolia.basescan.org/tx/${w.txHash}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ marginLeft: 8, color: "var(--accent, #2c7be5)" }}
                >
                  BaseScan ↗
                </a>
              </>
            ) : (
              "—"
            )
          }
        />
        <Row k="blockNumber" v={w.blockNumber != null ? String(w.blockNumber) : "—"} />
        <Row
          k="rejectionReason"
          v={
            w.rejectionReason ? (
              <span style={{ whiteSpace: "pre-wrap" }}>{w.rejectionReason}</span>
            ) : (
              "—"
            )
          }
        />
      </tbody>
    </table>
  );
}

function Timeline({ w }: { w: AffiliateWithdrawalRow }) {
  const steps: ReadonlyArray<{ label: string; at: string | null; reached: boolean }> = [
    { label: "Requested", at: w.requestedAt, reached: true },
    {
      label: "Approved",
      at: w.approvedAt,
      reached: w.status === "approved" || w.status === "paid",
    },
    { label: "Paid", at: w.paidAt, reached: w.status === "paid" },
  ];
  const rejected = w.status === "rejected";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12 }}>
      {steps.map((s) => (
        <div key={s.label} style={{ display: "flex", gap: 8 }}>
          <span
            style={{
              display: "inline-block",
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: s.reached ? "var(--ok, #0a9444)" : "var(--border, #ccc)",
            }}
            aria-hidden
          />
          <span style={{ width: 100, color: s.reached ? "var(--fg)" : "var(--fg-dim)" }}>
            {s.label}
          </span>
          <code style={{ color: "var(--fg-dim)" }}>{s.at ?? "—"}</code>
        </div>
      ))}
      {rejected && (
        <div style={{ display: "flex", gap: 8 }}>
          <span
            style={{
              display: "inline-block",
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: "var(--danger, #d23f3f)",
            }}
            aria-hidden
          />
          <span style={{ width: 100, color: "var(--danger, #d23f3f)" }}>Rejected</span>
          <code style={{ color: "var(--fg-dim)" }}>{w.rejectedAt ?? "—"}</code>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <tr style={{ borderTop: "1px solid var(--border)" }}>
      <td style={{ padding: "6px 8px", color: "var(--fg-dim)", width: 200 }}>{k}</td>
      <td style={{ padding: "6px 8px", wordBreak: "break-all" }}>{v}</td>
    </tr>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div
      role="alert"
      style={{
        padding: 12,
        border: "1px solid var(--danger, #d23f3f)",
        background: "rgba(210, 63, 63, 0.06)",
        color: "var(--danger, #d23f3f)",
        borderRadius: 4,
        fontSize: 13,
      }}
    >
      {text}
    </div>
  );
}

function primaryBtnStyle(enabled: boolean) {
  return {
    padding: "6px 16px",
    fontSize: 13,
    background: "var(--accent, #2c7be5)",
    color: "white",
    border: "none",
    borderRadius: 3,
    cursor: enabled ? ("pointer" as const) : ("default" as const),
    opacity: enabled ? 1 : 0.5,
    fontWeight: 600,
  };
}

function dangerBtnStyle(enabled: boolean) {
  return {
    padding: "6px 16px",
    fontSize: 13,
    background: "var(--danger, #d23f3f)",
    color: "white",
    border: "none",
    borderRadius: 3,
    cursor: enabled ? ("pointer" as const) : ("default" as const),
    opacity: enabled ? 1 : 0.5,
    fontWeight: 700,
  };
}

function neutralBtnStyle(enabled = true) {
  return {
    padding: "6px 16px",
    fontSize: 13,
    background: "transparent",
    color: "var(--danger, #d23f3f)",
    border: "1px solid var(--danger, #d23f3f)",
    borderRadius: 3,
    cursor: enabled ? ("pointer" as const) : ("default" as const),
    opacity: enabled ? 1 : 0.5,
    fontWeight: 600,
  };
}
